import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  DialogActions,
  Paper,
  CssBaseline,
  ThemeProvider,
  Stack,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { fetchPendingNotifications } from '../../actions/notification/notificationList';
import { upsertNotification } from '../../actions/notification/notificationUpsert';
import Notification from '../../models/Notification';
import { useThemeContext } from '../../theme/ThemeContext';
import { LaunchOutlined, MarkEmailReadOutlined, NotificationsActiveOutlined, SnoozeOutlined } from '@mui/icons-material';


const POLL_INTERVAL_MS = process.env.POLL_INTERVAL_MS ? Number(process.env.POLL_INTERVAL_MS) : 300000;

const MassNotificationPopup: React.FC = () => {
  const { t } = useTranslation('notification');
  const dispatch = useDispatch();
  const history = useHistory();
  const { theme } = useThemeContext()
  const [notification, setNotification] = useState<Notification | null>(null);

  useEffect(() => {
    const loadNotification = async () => {
      await dispatch(
        fetchPendingNotifications('?type=M&status=U', (response) => {
          const firstUnread =
            response?.notifications?.find?.((n) => n.status === 'U') || null;
          setNotification(firstUnread);
        })
      );
    };

    loadNotification();

    const interval = setInterval(loadNotification, POLL_INTERVAL_MS > 0 ? POLL_INTERVAL_MS : 300000)
    return () => {
      clearInterval(interval)
    }

  }, [dispatch]);

  if (!notification) return null;

  const markAsReadAnd = (callback: () => void) => {
    dispatch(
      upsertNotification({ ...notification, status: 'R' }, () => {
        setNotification(null);
        callback();
      })
    );
  };

  const closeDialog = (callback: () => void) => {
    setNotification(null);
    callback();
  };

return (
  <ThemeProvider theme={theme}>
    <CssBaseline />

    <Dialog open onClose={(event, reason) => {
      if (reason !== 'backdropClick') {
        closeDialog(() => { });
      }
    }}
      maxWidth="lg"
      scroll="body"
      PaperProps={{ style: { backgroundColor: theme.palette.background.default, color: theme.palette.primary.main } }}
      disableEscapeKeyDown>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <NotificationsActiveOutlined />
          <Typography component='span' variant="h5">{notification.title || t('notification')}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Paper elevation={2} sx={{ margin: "10px", padding: "2px 2px 2px 2px", borderRadius: "5px", bgcolor: theme.palette.success.main }}>
          <Paper elevation={2}
            style={{
              padding: "15px 15px 15px 15px", display: "flex", flexDirection: "column",
              textAlign: "justify", justifyContent: "space-around",
              borderRadius: "5px", margin: "2px"
            }}>
            <Typography sx={{ whiteSpace: 'pre-line' }} textAlign='center' variant="h6">{notification.message}</Typography>
          </Paper>
        </Paper>
      </DialogContent>
      <DialogActions>
        {notification.url &&
          <Tooltip title={t('getUrl')} placement="top" arrow>
            <Button
              variant="contained" color='secondary' sx={{ textTransform: "none", borderRadius: '8px' }}
              onClick={() => 
                notification.url && notification.url.includes('http') ? window.open(notification.url!, "_blank", "noopener,noreferrer") : history.push(notification.url!)}>
              <LaunchOutlined sx={{ cursor: 'pointer' }} />
            </Button>
          </Tooltip>
        }
        <Tooltip title={t('snooze')} placement="top" arrow>
          <Button
            variant="contained" color='secondary' sx={{ textTransform: "none", borderRadius: '8px' }} onClick={() => closeDialog(() => { })}>
            <SnoozeOutlined />
          </Button>
        </Tooltip>
        <Tooltip title={t('markAsRead')} placement="top" arrow>
          <Button
            variant="contained" color='success' sx={{ textTransform: "none", borderRadius: '8px' }} onClick={() => markAsReadAnd(() => { })}>
            <MarkEmailReadOutlined />
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  </ThemeProvider>
);
};

export default MassNotificationPopup;
